import { useState } from "react";
import { AlertTriangle, Clock, Flame, Phone, Mail, MessageSquare, Users, MapPin, StickyNote } from "lucide-react";
import { shortDate } from "@/lib/format";
import { STAGES, stageMeta, tempMeta, TEMPERATURES, daysSinceTouch, followUpOverdue, money0, activityLabel } from "./pipeline";

const TYPE_ICON = { call: Phone, email: Mail, text: MessageSquare, meeting: Users, site_visit: MapPin, note: StickyNote };

// Kanban board — one column per open stage; native HTML5 drag between columns.
export function LeadPipelineBoard({ leads, onMove, onOpen }) {
  const [over, setOver] = useState(null);
  const byStage = (s) => leads.filter(l => l.status === s);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 p-3" data-testid="lead-pipeline-board">
      {STAGES.map(stage => {
        const col = byStage(stage.value);
        const total = col.reduce((s, l) => s + (Number(l.estimated_value) || 0), 0);
        return (
          <div key={stage.value}
            className={`rounded-xl border bg-slate-50/60 min-h-[220px] flex flex-col transition-colors ${over === stage.value ? "border-blue-400 bg-blue-50/40" : "border-slate-200"}`}
            onDragOver={(e) => { e.preventDefault(); if (over !== stage.value) setOver(stage.value); }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => { e.preventDefault(); setOver(null); const id = e.dataTransfer.getData("text/lead-id"); if (id) onMove(id, stage.value); }}
            data-testid={`pipeline-column-${stage.value}`}>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200/70">
              <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
              <span className="text-xs font-semibold text-slate-700">{stage.label}</span>
              <span className="text-[11px] text-slate-500 ml-auto tabular-nums" data-testid={`pipeline-count-${stage.value}`}>{col.length} · {money0(total)}</span>
            </div>
            <div className="p-2 space-y-2 flex-1">
              {col.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6">Drop a lead here</div>}
              {col.map(lead => <LeadCard key={lead.id} lead={lead} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LeadCard({ lead, onOpen }) {
  const days = daysSinceTouch(lead);
  const overdue = followUpOverdue(lead);
  const temp = TEMPERATURES.find(t => t.value === lead.temperature);
  const Icon = TYPE_ICON[lead.last_activity_type] || StickyNote;
  return (
    <div draggable onDragStart={(e) => { e.dataTransfer.setData("text/lead-id", lead.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={() => onOpen(lead)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(lead)}
      className={`rounded-lg border bg-white p-2.5 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] border-slate-200`}
      data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 leading-tight truncate">{lead.company_name}</div>
          <div className="text-[11px] text-slate-500 truncate">{lead.contact_name}{lead.account_manager ? ` · ${lead.account_manager}` : ""}</div>
        </div>
        {temp && <span className={`${temp.cls} shrink-0`} style={{ fontSize: 10, padding: "1px 6px" }}>{temp.label}</span>}
      </div>
      <div className="flex items-center gap-2 mt-2 text-[11px]">
        <span className="font-medium text-slate-700 tabular-nums">{money0(lead.estimated_value)}</span>
        {lead.next_follow_up && !lead.follow_up_complete && (
          <span className={`inline-flex items-center gap-0.5 ${overdue ? "text-rose-600 font-medium" : "text-blue-600"}`} data-testid={`lead-card-followup-${lead.id}`}>
            <Clock className="w-3 h-3" />{shortDate(lead.next_follow_up)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 mt-1.5 text-[11px] text-slate-500 min-w-0" data-testid={`lead-card-last-${lead.id}`}>
        <Icon className="w-3 h-3 shrink-0" />
        <span className="truncate">{lead.last_activity_at ? `${activityLabel(lead.last_activity_type)}${lead.last_activity_summary ? ` — ${lead.last_activity_summary}` : ""}` : "No activity logged"}</span>
        <span className="ml-auto shrink-0 tabular-nums">{days === null ? "" : days === 0 ? "today" : `${days}d`}</span>
      </div>
    </div>
  );
}

// Single pastel stage badge (house badge rule) — used in the Leads table, the activity panel and the Actions menu.
// fixed = grid variant (same 90px × 24px chip as the Proposals status column)
const PILL_AUTO = "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium";
export const PILL_FIXED = "inline-flex h-6 w-[90px] items-center justify-center whitespace-nowrap rounded-full border text-[10px] font-medium";

export function StagePill({ status, fixed = false }) {
  const m = stageMeta(status);
  return <span className={`${fixed ? PILL_FIXED : PILL_AUTO} ${m.pill}`}>{m.label}</span>;
}

// Cold / Warm / Hot in the same badge shape.
export function TempPill({ temperature, testId, fixed = false }) {
  const m = tempMeta(temperature);
  if (!m) return <span className="text-slate-400">—</span>;
  return <span className={`${fixed ? PILL_FIXED : PILL_AUTO} ${m.pill}`} data-testid={testId}>{m.label}</span>;
}

export function HeatIcon({ temperature }) {
  if (!temperature) return null;
  const c = { cold: "text-sky-500", warm: "text-amber-500", hot: "text-rose-500" }[temperature];
  return <Flame className={`w-3.5 h-3.5 ${c}`} title={temperature} />;
}
